import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { type PhoneCallView, usePhoneCallFilterQuery } from '../../../queries/PhoneCall/usePhoneCallFilterQuery';
import { Button } from '../../../web/components-v2/Button';
import { Pill } from '../../../web/components-v2/Pill';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { PhoneCallFilterCriteriaProperty } from '../../viewModels/phonecall';
import {
	isJudgedCall,
	loggedOutcomeLabel,
	notScoredLabel,
	scoreTier,
	selectJudgeCalls,
	tierPillColor,
} from './scoreboardMetrics';
import { styleSheet } from './scoreboard.styles';
import { formatCallDuration, formatCallTimestamp } from './callDisplay';
import { CallLogLink, ReviewLink } from './callLinks';
import { ScoreboardCallThreads } from './ScoreboardCallThreads';

const PAGE_SIZE = 100;

interface IProps {
	userId: string;
	repName: string;
	start: string;
	end: string;
	followedScore: number;
}

export function ScoreboardRepCalls({ userId, repName, start, end, followedScore }: IProps) {
	const [expandedCallId, setExpandedCallId] = React.useState<string | null>(null);
	const criteria = React.useMemo(
		() => [
			{ property: PhoneCallFilterCriteriaProperty.User, value: userId },
			{ property: PhoneCallFilterCriteriaProperty.Date, op: Api.FilterOperator.Gt, value: start },
			{ property: PhoneCallFilterCriteriaProperty.Date, op: Api.FilterOperator.Lt, value: end },
			{ property: PhoneCallFilterCriteriaProperty.AdherenceJudged, value: 'true' },
		],
		[userId, start, end]
	);
	const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = usePhoneCallFilterQuery({
		criteria,
		pageSize: PAGE_SIZE,
	});

	if (isLoading) {
		return <LoadingSpinner type='small' />;
	}
	if (isError) {
		return <div className={css(styleSheet.state)}>Couldn&rsquo;t load {repName}&rsquo;s calls.</div>;
	}

	const calls = selectJudgeCalls(data?.calls ?? []);

	const renderCall = (view: PhoneCallView) => {
		const { phoneCall } = view;
		const result = phoneCall.coachingAdherenceResult;
		const judgment = result?.judgment;
		const expanded = expandedCallId === phoneCall.id;
		const duration = formatCallDuration(phoneCall.duration);
		const outcome = loggedOutcomeLabel(result?.loggedOutcome);

		return (
			<li key={phoneCall.id} className={css(styleSheet.call)}>
				<div className={css(styleSheet.callRow)}>
					{isJudgedCall(view) && result ? (
						<Pill
							variant='tag'
							size='small'
							color={tierPillColor(scoreTier(result.score, followedScore))}
							label={String(result.score)}
						/>
					) : (
						<Pill variant='tag' size='small' color='gray' label={notScoredLabel(result?.notScoredReason)} />
					)}
					<span className={css(styleSheet.callMeta)}>{formatCallTimestamp(phoneCall.creationDate)}</span>
					{duration ? <span className={css(styleSheet.callMeta)}>{duration}</span> : null}
					{outcome ? <span className={css(styleSheet.callOutcome)}>{outcome}</span> : null}
					<div className={css(styleSheet.callActions)}>
						{judgment ? (
							<Button
								variant='ghost'
								size='sm'
								aria-expanded={expanded}
								onClick={() => setExpandedCallId(expanded ? null : (phoneCall.id ?? null))}
							>
								{expanded ? 'Hide cards' : 'Show cards'}
							</Button>
						) : null}
						<ReviewLink aiConversationId={phoneCall.aiConversationId} />
						<CallLogLink phoneCallId={phoneCall.id} />
					</div>
				</div>
				{expanded && judgment ? <ScoreboardCallThreads judgment={judgment} /> : null}
			</li>
		);
	};

	return (
		<div className={css(styleSheet.callList)}>
			{calls.length === 0 ? (
				<div className={css(styleSheet.state)}>
					{`The adherence judge scored none of ${repName}’s calls in this range.`}
				</div>
			) : (
				<ul className={css(styleSheet.callList, styleSheet.listReset)} aria-label={`${repName}'s scored calls`}>
					{calls.map(renderCall)}
				</ul>
			)}
			{hasNextPage ? (
				<Button
					variant='secondary'
					size='sm'
					styles={[styleSheet.loadMore]}
					onClick={() => fetchNextPage()}
					disabled={isFetchingNextPage}
				>
					{isFetchingNextPage ? 'Loading…' : 'Load more'}
				</Button>
			) : null}
		</div>
	);
}
